import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanSearch, ScanLine, Tag, LogOut, PackageX, Scale } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { isLow, vals } from "@/lib/format";
import { parseScaleBarcode } from "@/lib/weigh";
import { ProductIcon, catColor } from "@/lib/catalog";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BigButton, inputCls } from "@/components/ui-kit";

function ExitDialog({ open, onClose }) {
  const { t, view, verifyPin } = useApp();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [err, setErr] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    for (const s of (view.staff || []).filter((x) => x.active)) {
      if (await verifyPin(s, pin)) { nav("/sell"); return; }
    }
    setErr(true);
    setPin("");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xs" data-testid="price-check-exit-dialog">
        <DialogTitle className="font-heading text-xl">{t("price_check_exit")}</DialogTitle>
        <form onSubmit={submit} className="space-y-3">
          <input autoFocus type="password" inputMode="numeric" maxLength={4} dir="ltr" value={pin} onChange={(e) => { setPin(e.target.value); setErr(false); }} className={`${inputCls} text-center text-2xl tracking-[0.5em]`} data-testid="price-check-exit-pin" />
          {err && <p className="text-destructive font-bold text-sm" data-testid="price-check-exit-error">{t("wrong_pin")}</p>}
          <BigButton type="submit" className="w-full" data-testid="price-check-exit-submit"><LogOut className="w-5 h-5" />OK</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Result({ hit }) {
  const { t, money, view } = useApp();
  if (hit.missing) {
    return <div className="text-center rise" data-testid="price-check-missing"><PackageX className="w-20 h-20 mx-auto text-[#F0444B]" /><div className="text-3xl font-bold mt-4">{t("not_found_barcode")}</div><div className="opacity-60 mt-1" dir="ltr">{hit.missing}</div></div>;
  }
  const p = hit.product;
  const promos = vals(view.promotions).filter((x) => x.active);
  const price = hit.total ?? p.price;
  const stock = p.stock <= 0 ? ["out_of_stock", "bg-[#F0444B]"] : isLow(p, view.shop?.low_stock_default) ? ["low_stock", "bg-[#E67E22]"] : ["in_stock", "bg-[#1F9D55]"];
  return (
    <div className="w-full max-w-3xl rise" data-testid="price-check-result">
      <div className="flex items-center gap-5">
        <span className="w-24 h-24 shrink-0 rounded-3xl grid place-items-center text-white" style={{ background: catColor(p.category) }}><ProductIcon product={p} className="w-12 h-12" /></span>
        <div className="min-w-0">
          <div className="text-3xl sm:text-5xl font-heading leading-tight" data-testid="price-check-name">{p.name}</div>
          <span className={`inline-block mt-3 px-3 py-1 rounded-full text-sm font-bold ${stock[1]}`} data-testid="price-check-stock">{t(stock[0])}</span>
        </div>
      </div>
      <div className="mt-8 rounded-3xl bg-white/10 p-6 sm:p-8">
        {hit.kg && <div className="text-xl opacity-80 flex items-center gap-2" dir="ltr"><Scale className="w-5 h-5" />{hit.kg.toFixed(3)} kg × {money(p.price)}/kg</div>}
        <div className="text-6xl sm:text-8xl font-black tracking-tight text-[#5EE3A1]" data-testid="price-check-price">{money(price)}</div>
        {p.sold_by_weight && !hit.kg && <div className="text-2xl font-bold opacity-80">/ kg</div>}
      </div>
      {promos.map((pr) => (
        <div key={pr.id} className="mt-4 rounded-2xl bg-[#F0444B] p-4 flex items-center justify-between gap-3 text-xl font-bold" data-testid="price-check-offer">
          <span className="flex items-center gap-2"><Tag className="w-6 h-6" />{t("offer")}: {pr.name} −{pr.percent}%</span>
          <span dir="ltr">{money(Math.round(price * (100 - pr.percent)) / 100)}</span>
        </div>
      ))}
    </div>
  );
}

export default function PriceCheck() {
  const { t, view } = useApp();
  const [hit, setHit] = useState(null);
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);
  const [exit, setExit] = useState(false);
  const input = useRef(null);
  const lookup = (c) => {
    const p = vals(view.products).find((x) => !x.deleted && x.barcode === c);
    if (p) return setHit({ product: p });
    const w = parseScaleBarcode(c, view.shop, view.products);
    return setHit(w || { missing: c });
  };
  useEffect(() => { if (!hit) return undefined; const id = setTimeout(() => setHit(null), 8000); return () => clearTimeout(id); }, [hit]);
  useEffect(() => {
    const id = setInterval(() => { if (!scan && !exit && document.activeElement !== input.current) input.current?.focus(); }, 800);
    return () => clearInterval(id);
  }, [scan, exit]);
  return (
    <div className="min-h-screen bg-[#0D2D3D] text-white flex flex-col" data-testid="price-check-page">
      <header className="h-16 px-4 flex items-center gap-3 border-b border-white/10">
        <ScanSearch className="w-7 h-7" />
        <span className="font-heading text-xl">{t("price_check")}</span>
        <span className="text-white/50 truncate">· {view.shop?.name}</span>
        <button type="button" onClick={() => setExit(true)} className="ms-auto w-10 h-10 rounded-xl bg-white/10 grid place-items-center" aria-label={t("price_check_exit")} data-testid="price-check-exit-btn"><LogOut className="w-5 h-5" /></button>
      </header>
      <main className="flex-1 grid place-items-center p-6">
        {hit ? <Result hit={hit} /> : (
          <div className="text-center" data-testid="price-check-idle">
            <div className="relative w-56 h-40 mx-auto mb-6 rounded-3xl border-4 border-dashed border-white/30 grid place-items-center overflow-hidden">
              <ScanLine className="w-20 h-20 text-white/80" strokeWidth={1.25} />
              <span className="absolute inset-x-4 h-0.5 bg-[#F0444B] shadow-[0_0_12px_#F0444B] price-scan-line" />
            </div>
            <div className="text-3xl sm:text-4xl font-heading">{t("scan_here")}</div>
            <div className="text-white/60 mt-2">{t("price_check_hint")}</div>
          </div>
        )}
      </main>
      <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) lookup(code.trim()); setCode(""); }} className="p-4 flex gap-2 max-w-xl w-full mx-auto">
        <input ref={input} autoFocus dir="ltr" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("manual_code")} className={`${inputCls} bg-white/10 border-white/20 text-white`} data-testid="price-check-input" />
        <button type="button" onClick={() => setScan(true)} className="h-12 px-4 rounded-xl bg-[#F0444B] font-bold flex items-center gap-2" data-testid="price-check-camera-btn"><ScanLine className="w-5 h-5" /></button>
      </form>
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onDetected={lookup} />
      <ExitDialog open={exit} onClose={() => setExit(false)} />
    </div>
  );
}
